import { anyPass, find, isEmpty, prop, propEq } from 'ramda';

import { api } from '@framework/constants/endpoints';
import {
    ADMIN_PORTAL_SECTION_SLUG,
    APPLICATION_PATHS,
    APPLICATION_SECTIONS_SLUG_TO_PATH
} from '@framework/constants/enums';
import { DROP_LAST_VISITED } from '@framework/constants/parameters';
import { idEquals } from '@framework/cookbook';
import {
    isAdmin,
    isAssistant,
    isContributor,
    isEditor,
    isGroupManager,
    isMember,
    isUserManagementAdmin
} from '@framework/members';
import { camelizeKeys } from '@framework/utils/camelize.utils';

const MEMBER_UUID = '__MEMBER_UUID__';

const URLS = {
    organisations: `${process.env.API_INTERNAL_URL}${api.organisations()}`,
    rooms: `${process.env.API_INTERNAL_URL}${api.rooms()}`,
    members: `${process.env.API_INTERNAL_URL}${api.member('uuid:__MEMBER_UUID__')}?related=true`,
    userAccount: `${process.env.API_INTERNAL_URL}${api.user()}`,
    topicHubRequestDetails: `${process.env.API_INTERNAL_URL}${api.loadRequestDetails(
        '__TOPIC_HUB_REQUEST_UUID__',
        '__ACTIVE_ROOM_UUID__'
    )}`,
    attendeeMeetingDetails: `${process.env.API_INTERNAL_URL}${api.meetingDetails(
        '__MEETING_UUID__',
        { mode: 'published' }
    )}`
};

const redirectTo = (path, ctx) => {
    ctx.res.redirect(path);
};

const getAdminPortalRequestedSection = (ctx) => {
    // For /organisations/[organisation]/[section] we can deduct what page user
    // want to visit, just by examining 'ctx.query.section'.
    if (ctx.query.section) {
        // Safety check if section will be non existing, i.e. user will make a typo in URL.
        if (!Object.values(ADMIN_PORTAL_SECTION_SLUG).includes(ctx.query.section)) {
            return null;
        }
        return ctx.query.section;
    }

    // For a 'ctx.pathname' different than /organisations/[organisation]/[section],
    // i.e. /organisations/[organisation]/members/[room] we don't have 'section'
    // prop inside 'ctx.query', so we need to check which page users want to access
    // based on 'ctx.pathname' property.
    const visitedSectionBasedOnPathname = new Map([
        ['/organisations/[organisation]', ADMIN_PORTAL_SECTION_SLUG.ADMINPORTAL],
        ['/organisations/[organisation]/billing', ADMIN_PORTAL_SECTION_SLUG.BILLING],
        ['/organisations/[organisation]/settings', ADMIN_PORTAL_SECTION_SLUG.SETTINGS],
        ['/organisations/[organisation]/members/[room]', ADMIN_PORTAL_SECTION_SLUG.MEMBERS],
        [
            '/organisations/[organisation]/members/[room]/group/[group]',
            ADMIN_PORTAL_SECTION_SLUG.MEMBERS
        ],
        ['/organisations/[organisation]/reports/[room]', ADMIN_PORTAL_SECTION_SLUG.REPORTS]
    ]);

    return visitedSectionBasedOnPathname.get(ctx.pathname) || null;
};

export const adminPortalRouteGuard = async (ctx, axiosServer) => {
    const { data: organisationsData } = await axiosServer.get(URLS.organisations, ctx);
    const allOrganisations = organisationsData?.results;

    const currentOrganisation = find(idEquals(ctx.query.organisation), allOrganisations);
    const permissions = prop('permissions', currentOrganisation);

    // If isn't an organisation user or gets organisation slug to which user has no connection
    if (!permissions) {
        redirectTo('/', ctx);
        return;
    }

    const visitedSection = getAdminPortalRequestedSection(ctx);

    const { canManageUsers, canAuditRooms, canViewInvoices, canRemoveEncryptionKeys } =
        camelizeKeys(permissions);

    // If user has all organisation permissions falsy (should not happen in real app)
    // or section check - to which users want to access - failed, then redirect to root
    if (
        !visitedSection ||
        (!canManageUsers && !canAuditRooms && !canViewInvoices && !canRemoveEncryptionKeys)
    ) {
        redirectTo('/?drop_last_page_visited=true', ctx);
        return;
    }

    // Typical case with at least one organisation permission and known section
    const permissionNeededToVisitSection = new Map([
        [
            ADMIN_PORTAL_SECTION_SLUG.ADMINPORTAL,
            canManageUsers || canAuditRooms || canViewInvoices || canRemoveEncryptionKeys
        ],
        [ADMIN_PORTAL_SECTION_SLUG.MEMBERS, canManageUsers],
        [ADMIN_PORTAL_SECTION_SLUG.REPORTS, canAuditRooms],
        [ADMIN_PORTAL_SECTION_SLUG.BILLING, canViewInvoices],
        [ADMIN_PORTAL_SECTION_SLUG.SETTINGS, canRemoveEncryptionKeys]
    ]);

    const isRedirectionNeeded = !permissionNeededToVisitSection.get(visitedSection);

    if (!isRedirectionNeeded) {
        return;
    }

    const organisationSlug = ctx.query.organisation ?? allOrganisations?.[0].id;

    if (!organisationSlug) {
        redirectTo('/?drop_last_page_visited=true', ctx);
        return;
    }
    let redirectToPath = `/organisations/${organisationSlug}/`;

    if (canManageUsers && !canAuditRooms && !canViewInvoices) {
        redirectToPath += 'members/';
    }
    if (!canManageUsers && canAuditRooms && !canViewInvoices) {
        redirectToPath += 'reports/';
    }
    if (!canManageUsers && !canAuditRooms && canViewInvoices) {
        redirectToPath += 'billing/';
    }
    if (!canManageUsers && !canAuditRooms && !canViewInvoices && canRemoveEncryptionKeys) {
        redirectToPath += 'settings/';
    }

    redirectTo(redirectToPath, ctx);
};

// common part of attendee and editor paths route guards
const attendeeEditorCommonRouteGuard = async (ctx, axiosServer) => {
    const { data: roomsData } = await axiosServer.get(URLS.rooms, ctx);

    const activeRoom = find(propEq(ctx.query.room, 'slug'), camelizeKeys(roomsData?.results));

    if (!activeRoom) {
        /**
         * This scenario can occur in the following situations:
         * - Users are not members of this room (either they have never been or they have been removed).
         * - The room no longer exists or never existed.
         * This situation commonly arises when users have a direct link to the room or when the
         * rooms_last_visited_ui state is pointing to a room they were previously a part of,
         * resulting in one of the above scenarios.
         * In all cases, we redirect users to the root page and discard the rooms_last_visited_ui state,
         * if present, so that the server can determine the appropriate redirect for the user.
         */
        redirectTo(`/?${DROP_LAST_VISITED}=true`, ctx);
        return { finished: true };
    }

    const memberUuid = activeRoom?.memberUuid;
    const roomBlockedByIpFiltering = activeRoom?.isUserBlockedByIpFiltering;

    // redirect for admin portal users, which does not have membership in a room
    if (!memberUuid) {
        redirectTo('/', ctx);
        return { finished: true };
    }

    // in case of a room which ip filtered, we cannot fetch member data, so in such case just
    // allow to enter a room and let user see a restriction message
    if (roomBlockedByIpFiltering) {
        return { finished: true };
    }

    const { data: memberData } = await axiosServer.get(
        URLS.members.replace(new RegExp(MEMBER_UUID, 'gi'), memberUuid),
        ctx
    );
    const results = camelizeKeys(memberData?.results);

    // if user has no membership for requested room, redirect to root
    if (!results || isEmpty(results)) {
        redirectTo('/', ctx);
        return { finished: true };
    }

    return { finished: false, results };
};

export const editorViewRouteGuard = async (ctx, axiosServer) => {
    const { finished, results } = await attendeeEditorCommonRouteGuard(ctx, axiosServer);
    if (finished) {
        return;
    }

    const splitUrl = ctx.asPath.split('/');
    const sectionWantedToAccess = splitUrl[4];

    const isMemberUmaAndIsMembersSection = (memberObj) =>
        isUserManagementAdmin(memberObj) &&
        sectionWantedToAccess === APPLICATION_SECTIONS_SLUG_TO_PATH.MEMBERS;
    const canVisitEditorView = anyPass([
        isAdmin,
        isGroupManager,
        isEditor,
        isMemberUmaAndIsMembersSection
    ])(results);
    const canVisitAttendeeMeetingsView = isMember(results) || isAssistant(results);
    const canVisitAttendeeTopicHubView = isContributor(results);

    // user was an admin and rooms_last_visited_ui user state entry points to editor view
    // page, but in the meantime member's role was changed to member or assistant
    if (!canVisitEditorView && canVisitAttendeeMeetingsView) {
        redirectTo(
            `/${APPLICATION_PATHS.ROOMS}/${ctx.query.room}/${APPLICATION_SECTIONS_SLUG_TO_PATH.MEETINGS}/`,
            ctx
        );
        return;
    }

    // user was an admin and rooms_last_visited_ui user state entry points to editor view
    // page, but in the meantime member's role was changed to contributor
    if (!canVisitEditorView && canVisitAttendeeTopicHubView) {
        redirectTo(
            `/${APPLICATION_PATHS.ROOMS}/${ctx.query.room}/${APPLICATION_SECTIONS_SLUG_TO_PATH.TOPIC_HUB}/`,
            ctx
        );
        return;
    }

    if (!canVisitEditorView) {
        redirectTo('/', ctx);
    }
};

export const attendeeMeetingRouteGuard = async (ctx, axiosServer) => {
    const MEETING_UUID = '__MEETING_UUID__';
    const splitUrl = ctx.asPath.split('/');
    const meetingId = ctx.query.uuid ?? splitUrl[4];

    try {
        await axiosServer.get(
            URLS.attendeeMeetingDetails.replace(new RegExp(MEETING_UUID, 'gi'), meetingId),
            ctx
        );
    } catch ({ response: { status } }) {
        if (status !== 200 && status !== 403) {
            redirectTo(
                `/${APPLICATION_PATHS.ROOMS}/${ctx.query.room}/${APPLICATION_SECTIONS_SLUG_TO_PATH.MEETINGS}/`,
                ctx
            );
        }
    }
};

export const attendeeViewRouteGuard = async (ctx, axiosServer) => {
    const { finished, results } = await attendeeEditorCommonRouteGuard(ctx, axiosServer);
    if (finished) {
        return;
    }

    const splitUrl = ctx.asPath.split('/');
    const sectionWantedToAccess = splitUrl[3];

    const isMemberContributorAndIsTopicHubSection = (memberObj) =>
        isContributor(memberObj) &&
        [APPLICATION_SECTIONS_SLUG_TO_PATH.TOPIC_HUB].includes(sectionWantedToAccess);
    const canVisitAttendeeView = anyPass([
        isAdmin,
        isGroupManager,
        isEditor,
        isMember,
        isAssistant,
        isMemberContributorAndIsTopicHubSection
    ])(results);

    // user was i.e. member and rooms_last_visited_ui user state entry points to other section
    // in attendee view, but in the meantime member's role was changed to contributor
    if (!isMemberContributorAndIsTopicHubSection(results) && isContributor(results)) {
        redirectTo(
            `/${APPLICATION_PATHS.ROOMS}/${ctx.query.room}/${APPLICATION_SECTIONS_SLUG_TO_PATH.TOPIC_HUB}/`,
            ctx
        );
        return;
    }

    if (!canVisitAttendeeView) {
        redirectTo('/', ctx);
    }
};

export const roomCreationRouteGuard = async (ctx, axiosServer) => {
    const { data: userAccountData } = await axiosServer.get(URLS.userAccount, ctx);

    const isStaff = propEq(true, 'isStaff');
    const canCreateRooms = isStaff(userAccountData?.results);

    if (!canCreateRooms) {
        redirectTo('/', ctx);
    }
};

export const topicHubDetailsRouteGuard = async (ctx, axiosServer) => {
    const TOPIC_HUB_REQUEST_UUID = '__TOPIC_HUB_REQUEST_UUID__';
    const ACTIVE_ROOM_UUID = '__ACTIVE_ROOM_UUID__';

    const splitUrl = ctx.asPath.split('/');
    const requestedPageRootSection = splitUrl[1];

    const { data: roomsData } = await axiosServer.get(URLS.rooms, ctx);
    const activeRoom = camelizeKeys(roomsData?.results).find(
        (room) => room.slug === ctx.query.room
    );

    const requestId = ctx.query.itemId;

    const topicHubNoAccessUrl = `/${APPLICATION_PATHS.ROOMS}/${ctx.query.room}/${APPLICATION_SECTIONS_SLUG_TO_PATH.TOPIC_HUB}/?error=no-access`;

    const urlToRedirect =
        requestedPageRootSection === APPLICATION_PATHS.ADMINISTRATOR
            ? `/${APPLICATION_PATHS.ADMINISTRATOR}${topicHubNoAccessUrl}`
            : topicHubNoAccessUrl;

    // this guard is done directly in the meeting outcomes entrypoints
    if (ctx.asPath.includes('meeting-outcomes')) return;

    try {
        await axiosServer.get(
            URLS.topicHubRequestDetails
                .replace(new RegExp(TOPIC_HUB_REQUEST_UUID, 'gi'), requestId)
                .replace(new RegExp(ACTIVE_ROOM_UUID, 'gi'), activeRoom.id),
            ctx
        );
    } catch ({ response: { status } }) {
        if (status !== 200) {
            redirectTo(urlToRedirect, ctx);
        }
    }
};

export const appRouteGuard = async (
    ctx,
    { axiosServer },
    adminPortalRouteGuardFn = adminPortalRouteGuard,
    attendeeViewRouteGuardFn = attendeeViewRouteGuard,
    editorViewRouteGuardFn = editorViewRouteGuard,
    roomCreationRouteGuardFn = roomCreationRouteGuard,
    topicHubDetailsRouteGuardFn = topicHubDetailsRouteGuard,
    attendeeMeetingRouteGuardFn = attendeeMeetingRouteGuard
) => {
    const splitUrl = ctx.asPath.split('/');
    const requestedPageRootPath = splitUrl[1];
    const requestedPageSection =
        requestedPageRootPath === APPLICATION_PATHS.ROOMS ? splitUrl[3] : splitUrl[4];

    if (
        requestedPageSection === APPLICATION_SECTIONS_SLUG_TO_PATH.MEETINGS &&
        requestedPageRootPath === APPLICATION_PATHS.ROOMS &&
        (ctx.query.uuid || splitUrl[4])
    ) {
        await attendeeMeetingRouteGuardFn(ctx, axiosServer);
    }

    if (requestedPageSection === APPLICATION_SECTIONS_SLUG_TO_PATH.TOPIC_HUB && ctx.query.itemId) {
        await topicHubDetailsRouteGuardFn(ctx, axiosServer);
    }

    if (requestedPageRootPath === APPLICATION_PATHS.ORGANISATIONS) {
        await adminPortalRouteGuardFn(ctx, axiosServer);
    }

    if (requestedPageRootPath === APPLICATION_PATHS.ADMINISTRATOR) {
        await editorViewRouteGuardFn(ctx, axiosServer);
    }

    if (requestedPageRootPath === APPLICATION_PATHS.ROOMS) {
        await attendeeViewRouteGuardFn(ctx, axiosServer);
    }

    if (requestedPageRootPath === APPLICATION_PATHS.ROOM_CREATION) {
        await roomCreationRouteGuardFn(ctx, axiosServer);
    }
};
